import {
  closestCenter,
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DraggableAttributes,
  type DraggableSyntheticListeners,
} from '@dnd-kit/core'
import {
  arrayMove,
  rectSortingStrategy,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { GripVertical } from 'lucide-react'
import type { ReactNode, PointerEvent as ReactPointerEvent } from 'react'

/** Distance the pointer must travel before a drag starts, so a plain click on
 *  the grip (or on a control inside a draggable row) still counts as a click. */
const DRAG_ACTIVATION_DISTANCE = 4

export function useDragSensors() {
  return useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: DRAG_ACTIVATION_DISTANCE },
    }),
  )
}

/**
 * Keeps a press on a control inside a self-dragging item from starting the
 * drag — a remove button on a picture, a field under it.
 */
export const stopDragPropagation = (event: ReactPointerEvent) =>
  event.stopPropagation()

/**
 * The grip a sortable row is picked up by.
 *
 * @single-use handed to the row by `SortableItem`, which is what callers render
 */
export function DragHandle({
  attributes,
  listeners,
  size = 14,
}: {
  attributes: DraggableAttributes
  listeners: DraggableSyntheticListeners
  size?: number
}) {
  return (
    <span
      {...attributes}
      {...listeners}
      // `inline-flex`, not `inline`: lucide renders a block-level SVG, and a
      // block inside an inline box splits the line around it — the grip came
      // out 64 px tall and inflated every sortable row to match.
      className="inline-flex cursor-grab p-0.5 text-faint"
    >
      <GripVertical size={size} />
    </span>
  )
}

/** What a drag means for the item it picks up. */
export type DragMode = 'sort' | 'move'

type SortableItemRenderProps = {
  handle: ReactNode
  isDragging: boolean
  /** True while a dragged item hovers this one. Only `move` mode shows it. */
  isOver: boolean
}

/**
 * One draggable row.
 *
 * `sort` reorders: the rows shift under the pointer to show the position the
 * item will take, and a grip is the affordance for choosing one. `move` drops
 * one item onto another and nothing changes position, so the whole row is the
 * handle and the row under the pointer lights up instead — a list with no
 * order of its own has no position to preview.
 */
export function SortableItem({
  id,
  gripSize = 14,
  mode = 'sort',
  fixed = false,
  grip = true,
  children,
}: {
  id: string | number
  gripSize?: number
  mode?: DragMode
  /**
   * Whether a `sort` item is picked up by a grip. `false` hands the whole item
   * the drag instead, for one that is itself the obvious thing to grab — a
   * picture in a gallery. The sensor's activation distance is what keeps a
   * click on such an item a click; a control inside it needs
   * `stopDragPropagation` so a press on it never reaches the drag.
   *
   * `move` already gives the whole row the drag and ignores this.
   */
  grip?: boolean
  /**
   * The item cannot be picked up but still takes a drop — a storey whose place
   * in the stack is derived, a folder that is only ever a destination. Leaving
   * it out of the context altogether would make it invisible to the pointer,
   * so the row under the cursor would report nothing and the drop be lost.
   */
  fixed?: boolean
  children: (props: SortableItemRenderProps) => ReactNode
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
    isOver,
  } = useSortable({ id, disabled: { draggable: fixed, droppable: false } })

  if (mode === 'move') {
    return (
      <div
        ref={setNodeRef}
        {...attributes}
        {...listeners}
        className={fixed ? undefined : 'cursor-grab'}
        style={{ opacity: isDragging ? 0.4 : 1 }}
      >
        {children({ handle: null, isDragging, isOver })}
      </div>
    )
  }

  const dragsItself = !grip && !fixed

  return (
    <div
      ref={setNodeRef}
      {...(dragsItself ? { ...attributes, ...listeners } : {})}
      className={dragsItself ? 'cursor-grab' : undefined}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.4 : 1,
      }}
    >
      {children({
        handle:
          grip && !fixed ? (
            <DragHandle
              attributes={attributes}
              listeners={listeners}
              size={gripSize}
            />
          ) : null,
        isDragging,
        isOver: false,
      })}
    </div>
  )
}

/**
 * A list whose rows can be dragged into another order.
 *
 * The wiring around a sortable list — the sensors, the guard against a drop on
 * itself, locating the two indices, `arrayMove` — was written out at six
 * places and was the same six times. What differs is only what happens to the
 * reordered array, so that is the one thing the caller passes.
 *
 * A dropped row stays where it was dropped because the write says so before
 * the server does: `useMutationWithInvalidation`'s `optimistic` writes the new
 * order into the cache the rows are drawn from, and takes it back out if the
 * write is refused. That is one rule for every reader of those rows — the
 * list, the card behind it, the tree — where an order held in this component
 * would have corrected only what it draws itself.
 *
 * Rows inside are `SortableItem`s keyed by the same `getId`.
 */
export function SortableList<T>({
  items,
  getId,
  onReorder,
  layout = 'list',
  children,
}: {
  items: readonly T[]
  getId: (item: T) => string | number
  /** The items in their new order. */
  onReorder: (items: T[]) => void
  /** `grid` for tiles that wrap; `list` for rows in one column. */
  layout?: 'list' | 'grid'
  children: ReactNode
}) {
  const sensors = useDragSensors()

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={({ active, over }: DragEndEvent) => {
        if (!over || active.id === over.id) return
        const from = items.findIndex(item => getId(item) === active.id)
        const to = items.findIndex(item => getId(item) === over.id)
        if (from === -1 || to === -1) return
        onReorder(arrayMove([...items], from, to))
      }}
    >
      <SortableContext
        items={items.map(getId)}
        strategy={
          layout === 'grid' ? rectSortingStrategy : verticalListSortingStrategy
        }
      >
        {children}
      </SortableContext>
    </DndContext>
  )
}

/**
 * The order as the server stores it: a position per row, counted from zero.
 *
 * Written out at thirteen call sites, every one of them `(item, index) => ({
 * id: item.id, sortOrder: index })` — and the drift was in what the row was
 * called, never in the rule.
 */
export function reindex(
  /** The rows in their new order, or their ids. */
  items: readonly (number | { id: number })[],
): { id: number; sortOrder: number }[] {
  return items.map((item, index) => ({
    id: typeof item === 'number' ? item : item.id,
    sortOrder: index,
  }))
}
